import React from 'react';
import { css } from '@emotion/css';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import { getAdaptedBaseCardContent } from '@/modules/content/card/utils/getAdaptedBaseCardContent';
import * as BaseCard from '@/modules/content/components/ContentCard/BaseCard';
import DefaultCard from '@/modules/content/components/ContentCard/DefaultCard';
import { DEFAULT_CARD_IMAGE_HEIGHT } from '@/modules/content/components/ContentCard/DefaultCard/styles';
import { renderProviderLabel } from '@/modules/content/modules/external-content/utils';
import { ResourceTypeEnum } from '@/types/schema';

import { renderProgressStatus } from '../DisplayCard/renderers';
import { cardSizeMobileStyles, cardSizeStyles } from '../styles';
import { ContentOverdueDueEnum } from '../utils/types';
import { NewDefaultCardProps } from './types';

export const imageStyles = (height: number) => css`
  height: ${height}rem;
`;

const EVENT_CONTENT_TYPES = [
  ResourceTypeEnum.OfflineEventContent,
  ResourceTypeEnum.ExternalEventContent,
  ResourceTypeEnum.LiveEventContent,
  ResourceTypeEnum.RecurringExternalEventContent,
  ResourceTypeEnum.RecurringLiveEventContent,
  ResourceTypeEnum.RecurringOfflineEventContent,
];

const PROVIDER_CLASSES_WITH_PROGRESS = { root: { className: 'mt-24' } };

//re move from here
const NewDefaultCard: React.FC<NewDefaultCardProps> = React.memo((props) => {
  const { className, imageProps, baseContent, content: contentProp, noDescription } = props;
  const { t } = useTranslation();

  const content = baseContent ?? getAdaptedBaseCardContent(t, contentProp);
  const isCompleted = content?.status?.label?.toUpperCase() == ContentOverdueDueEnum.Completed;
  const { isMobile } = useScreen();

  const imageHeight = imageStyles(
    isMobile ? DEFAULT_CARD_IMAGE_HEIGHT.mobile : DEFAULT_CARD_IMAGE_HEIGHT.web,
  );

  const overrideDefaultStyle =
    !isMobile && noDescription
      ? cn(
          cardSizeStyles({ height: 44.6, width: 33.9 }),
          cardSizeMobileStyles({
            height: 29.6,
            width: 26.4,
          }),
        )
      : undefined;

  return (
    <DefaultCard
      className={cn(className, 'flex-1')}
      content={content}
      overrideDefaultStyle={overrideDefaultStyle}>
      <BaseCard.Image
        alt="card image"
        className={cn('w-full', imageHeight)}
        containerClassName="rounded-t-16"
        height={imageProps.height}
        width={imageProps.width}
      />
      <Stack alignItems="start" className="flex-1 p-20 pt-16 xs:px-16" direction="col">
        <BaseCard.Label />
        <BaseCard.Name className="mt-6 xs:mt-4" numberOfLines={2} />
        {isMobile || (content.contentType === ResourceTypeEnum.Scorm && content.progress) ? null : (
          <BaseCard.Description containerClassName="mt-8" numberOfLines={2} />
        )}
        {content.contentType === ResourceTypeEnum.ExternalContent && !baseContent
          ? renderProviderLabel(
              props.content.provider,
              content.progress ? PROVIDER_CLASSES_WITH_PROGRESS : undefined,
            )
          : null}
        {content.contentType === ResourceTypeEnum.Exercise
          ? null
          : renderProgressStatus(t, content.progress)}
        {content?.contentType && EVENT_CONTENT_TYPES.includes(content?.contentType) ? (
          <BaseCard.Description numberOfLines={2} />
        ) : null}
      </Stack>
      {isCompleted ? null : <BaseCard.Status className="absolute top-16 start-16" />}
    </DefaultCard>
  );
});

export default NewDefaultCard;
